import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

export interface PanelSize { width: number; height: number }
export type ResizeAxis = 'both' | 'x' | 'y';

/** Marge gardée autour du panneau (1,5rem de chaque côté) et bornes du redimensionnement. */
export const LIA_PANEL_MARGIN = 48;
export const LIA_PANEL_MIN: PanelSize = { width: 360, height: 420 };
export const LIA_PANEL_DEFAULT: PanelSize = { width: 448, height: 666 };
export const LIA_PANEL_LARGE: PanelSize = { width: 800, height: 1_400 };
const STORAGE_KEY = 'taki:lia:taille';
const KEY_STEP = 32;

export function clampPanelSize(size: PanelSize, viewport: PanelSize): PanelSize {
  const maxWidth = Math.max(LIA_PANEL_MIN.width, viewport.width - LIA_PANEL_MARGIN);
  const maxHeight = Math.max(LIA_PANEL_MIN.height, viewport.height - LIA_PANEL_MARGIN);
  return {
    width: Math.round(Math.min(maxWidth, Math.max(LIA_PANEL_MIN.width, size.width))),
    height: Math.round(Math.min(maxHeight, Math.max(LIA_PANEL_MIN.height, size.height))),
  };
}

/** Taille retenue d'une ouverture à l'autre ; `null` = taille par défaut. Une valeur illisible est ignorée. */
export function readPanelSize(raw: string | null): PanelSize | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (value && typeof value === 'object' && 'width' in value && 'height' in value &&
        typeof value.width === 'number' && typeof value.height === 'number' &&
        Number.isFinite(value.width) && Number.isFinite(value.height)) return { width: value.width, height: value.height };
  } catch { /* Valeur corrompue : taille par défaut. */ }
  return null;
}

/** Le panneau est "agrandi" quand il occupe (presque) toute la place que la fenêtre lui laisse. */
export function isPanelLarge(size: PanelSize, viewport: PanelSize): boolean {
  const large = clampPanelSize(LIA_PANEL_LARGE, viewport);
  const normal = clampPanelSize(LIA_PANEL_DEFAULT, viewport);
  if (large.width <= normal.width + 8 && large.height <= normal.height + 8) return false;
  return size.width >= large.width - 8 && size.height >= large.height - 8;
}

function viewportSize(): PanelSize {
  return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * Taille du panneau sur ordinateur : poignée en haut à gauche (le panneau est
 * ancré en bas à droite), flèches du clavier sur la poignée, bouton agrandir.
 * Sur téléphone le panneau est plein écran : cette taille ne s'applique pas.
 */
export function useLiaPanelSize() {
  const [saved, setSaved] = useState<PanelSize | null>(() => {
    try { return readPanelSize(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
  });
  const [viewport, setViewport] = useState<PanelSize>(viewportSize);
  const [resizing, setResizing] = useState(false);
  const drag = useRef<{ x: number; y: number; start: PanelSize; pointer: number; axis: ResizeAxis; last: PanelSize } | null>(null);

  useEffect(() => {
    const update = (): void => setViewport(viewportSize());
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const size = clampPanelSize(saved ?? LIA_PANEL_DEFAULT, viewport);
  const large = isPanelLarge(size, viewport);

  const persist = useCallback((next: PanelSize | null): void => {
    setSaved(next);
    try {
      if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* Stockage indisponible : la taille vaut pour cette page. */ }
  }, []);

  const toggleLarge = useCallback((): void => {
    persist(large ? null : clampPanelSize(LIA_PANEL_LARGE, viewportSize()));
  }, [large, persist]);

  const dragged = (current: NonNullable<typeof drag.current>, event: PointerEvent<HTMLElement>): PanelSize => clampPanelSize({
    width: current.axis === 'y' ? current.start.width : current.start.width + current.x - event.clientX,
    height: current.axis === 'x' ? current.start.height : current.start.height + current.y - event.clientY,
  }, viewportSize());

  const handle = (axis: ResizeAxis) => ({
    onPointerDown(event: PointerEvent<HTMLElement>): void {
      if (event.button !== 0) return;
      event.preventDefault();
      try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* Pointeur déjà relâché. */ }
      drag.current = { x: event.clientX, y: event.clientY, start: size, pointer: event.pointerId, axis, last: size };
      setResizing(true);
    },
    onPointerMove(event: PointerEvent<HTMLElement>): void {
      const current = drag.current;
      if (!current || current.pointer !== event.pointerId) return;
      current.last = dragged(current, event);
      setSaved(current.last);
    },
    onPointerUp(event: PointerEvent<HTMLElement>): void {
      const current = drag.current;
      if (!current || current.pointer !== event.pointerId) return;
      drag.current = null;
      setResizing(false);
      persist(dragged(current, event));
    },
    onPointerCancel(): void {
      const current = drag.current;
      if (!current) return;
      drag.current = null;
      setResizing(false);
      persist(current.last);
    },
    onDoubleClick(): void { persist(null); },
  });

  const onHandleKey = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Home') { event.preventDefault(); persist(null); return; }
    const delta = ({ ArrowLeft: [KEY_STEP, 0], ArrowRight: [-KEY_STEP, 0], ArrowUp: [0, KEY_STEP], ArrowDown: [0, -KEY_STEP] } as Record<string, [number, number]>)[event.key];
    if (!delta) return;
    event.preventDefault();
    persist(clampPanelSize({ width: size.width + delta[0], height: size.height + delta[1] }, viewportSize()));
  };

  const max = clampPanelSize({ width: Number.MAX_SAFE_INTEGER, height: Number.MAX_SAFE_INTEGER }, viewport);
  return { size, max, custom: saved !== null, large, resizing, toggleLarge, handle, onHandleKey };
}
